import { faker } from "@faker-js/faker/locale/pt_BR";

function generateCompany() {
  return {
    id: faker.number.int({ min: 1, max: 100 }),
    name: faker.company.name(),
    cnpj: faker.string.numeric(14),
    city: faker.location.city(),
    state: faker.location.state({ abbreviated: true }),
    active: true,
  };
}

function generateUser() {
  return {
    id: faker.number.int({ min: 1, max: 500 }),
    name: faker.person.fullName(),
    email: faker.internet.email(),
    isAdmin: faker.datatype.boolean(0.1),
    abilities: [],
    companies: [generateCompany()],
    created_at: faker.date.past().toISOString(),
  };
}

function generateSystemSetting(key, type, value) {
  return {
    id: faker.number.int({ min: 1, max: 200 }),
    key,
    value,
    type,
    description: faker.lorem.sentence(),
  };
}

function generatePayrollMapping() {
  const codlanc = faker.string.numeric(4);
  return {
    id: faker.number.int({ min: 1, max: 500 }),
    source_id: codlanc,
    source_description: `${codlanc} - ${faker.finance.transactionDescription()}`,
    release_type: faker.helpers.arrayElement(["N", "E"]),
    company_id: faker.helpers.maybe(() => faker.number.int({ min: 1, max: 20 }), { probability: 0.4 }),
    company_name: faker.company.name(),
    events: Array.from({ length: faker.number.int({ min: 1, max: 4 }) }, () => ({
      id: faker.number.int({ min: 1, max: 1000 }),
      event_code: faker.string.numeric(3),
      event_description: faker.finance.transactionDescription(),
    })),
  };
}

function generatePayrollBatch() {
  const total = faker.number.int({ min: 10, max: 200 });
  const processed = faker.number.int({ min: 0, max: total });
  const status = processed === total ? "success" : processed === 0 ? "pending" : "processing";
  return {
    id: faker.number.int({ min: 1, max: 500 }),
    company_id: faker.number.int({ min: 1, max: 20 }),
    company_name: faker.company.name(),
    period: faker.date.recent({ days: 60 }).toISOString().slice(0, 7),
    total_records: total,
    processed_records: processed,
    status,
    created_at: faker.date.recent().toISOString(),
  };
}

function generateReconciliation() {
  const rmTotal = faker.number.float({ min: 1000, max: 100000, fractionDigits: 2 });
  const diff = faker.helpers.maybe(() => faker.number.float({ min: 0.01, max: 500, fractionDigits: 2 }), { probability: 0.3 }) || 0;
  return {
    company_id: faker.number.int({ min: 1, max: 20 }),
    company_name: faker.company.name(),
    source_id: faker.string.numeric(4),
    source_description: faker.finance.transactionDescription(),
    rm_total: rmTotal,
    bordero_total: rmTotal - diff,
    difference: diff,
    status: diff === 0 ? "conciliado" : "divergencia",
  };
}

function generateCashConciliation() {
  return {
    id: faker.number.int({ min: 1, max: 1000 }),
    company_id: faker.number.int({ min: 1, max: 20 }),
    company_name: faker.company.name(),
    date: faker.date.recent({ days: 30 }).toISOString().slice(0, 10),
    system_total: faker.number.float({ min: 5000, max: 200000, fractionDigits: 2 }),
    bank_total: faker.number.float({ min: 5000, max: 200000, fractionDigits: 2 }),
    status: faker.helpers.arrayElement(["open", "closed", "pending"]),
    pdv: faker.string.numeric(3),
    operator: faker.person.firstName(),
  };
}

function paginateData(data, page, perPage) {
  const start = (page - 1) * perPage;
  return {
    data: data.slice(start, start + perPage),
    meta: {
      current_page: page,
      last_page: Math.ceil(data.length / perPage),
      per_page: perPage,
      total: data.length,
    },
  };
}

const MOCK_SETTINGS = [
  generateSystemSetting("rm.communication.enabled", "boolean", "1"),
  generateSystemSetting("rm.allow_expired_due_date_integration", "boolean", "0"),
  generateSystemSetting("protheus.integration.enabled", "boolean", "1"),
  generateSystemSetting("protheus.timeout", "integer", "120"),
  generateSystemSetting("protheus.retry_times", "integer", "3"),
  generateSystemSetting("recebimento.use_all_invoice", "boolean", "0"),
  generateSystemSetting("mobile.max_shortcut_modules", "integer", "3"),
  generateSystemSetting("conciliation.limit_inconsistencies", "integer", "50"),
  generateSystemSetting("mail.purchase_orders.enabled", "boolean", "1"),
  generateSystemSetting("portals.2fa.enabled", "boolean", "1"),
];

/**
 * Route handlers: URL pattern -> mock response generator
 * Patterns use simple matching (startsWith / includes)
 */
const handlers = [
  {
    match: (url) => url.includes("/system-settings"),
    methods: ["get"],
    handler: () => ({ data: MOCK_SETTINGS }),
  },
  {
    match: (url) => url.includes("/rm/mappings") || url.includes("/rm-mapping"),
    methods: ["get"],
    handler: () => ({
      data: Array.from({ length: 25 }, generatePayrollMapping),
    }),
  },
  {
    match: (url) => url.includes("/payroll-integration") || url.includes("/payroll-sync"),
    methods: ["get"],
    handler: (config) => {
      const batches = Array.from({ length: 15 }, generatePayrollBatch);
      const page = parseInt(config?.params?.page || 1);
      return paginateData(batches, page, 10);
    },
  },
  {
    match: (url) => url.includes("/reconciliation"),
    methods: ["get"],
    handler: () => ({
      data: Array.from({ length: 12 }, generateReconciliation),
      summary: {
        rm_total: faker.number.float({ min: 100000, max: 1000000, fractionDigits: 2 }),
        bordero_total: faker.number.float({ min: 100000, max: 1000000, fractionDigits: 2 }),
        conciliated: faker.number.int({ min: 5, max: 12 }),
        total: 12,
      },
    }),
  },
  {
    match: (url) => url.includes("/cash-conciliation"),
    methods: ["get"],
    handler: () => ({
      data: Array.from({ length: 20 }, generateCashConciliation),
    }),
  },
  {
    match: (url) => url.includes("/users"),
    methods: ["get"],
    handler: () => ({
      data: Array.from({ length: 10 }, generateUser),
    }),
  },
  {
    match: (url) => url.includes("/companies"),
    methods: ["get"],
    handler: () => ({
      data: Array.from({ length: 8 }, generateCompany),
    }),
  },
  {
    match: (url) => url.includes("/menus"),
    methods: ["get"],
    handler: () => null, // never mock menus - let real API handle
  },
  {
    match: (url) => url.includes("/auth"),
    methods: ["post"],
    handler: () => null, // never mock auth
  },
];

/**
 * Find a mock handler for the given request
 * Returns the mock response data, or null if no handler matches
 */
export function findMockHandler(url, method) {
  const m = (method || "get").toLowerCase();
  for (const h of handlers) {
    if (h.match(url) && h.methods.includes(m)) {
      return h.handler;
    }
  }
  return null;
}

export {
  generateCompany,
  generateUser,
  generateSystemSetting,
  generatePayrollMapping,
  generatePayrollBatch,
  generateReconciliation,
  generateCashConciliation,
};
